import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import PropTypes from 'prop-types';

import { getPaymentScheduleTableConfig } from '../Servicing.config';
import { ScheduleStatusEnum } from '../Servicing.constants';

import { PaymentScheduleTestIds } from './PaymentSchedule.testIds';

const ROWS_PER_PAGE = 10;

const getInitialPage = data => {
  const nextUnpaidIndex = data.findIndex(row => row.status !== ScheduleStatusEnum.PAID);

  if (nextUnpaidIndex === -1) {
    return 0;
  }

  return Math.floor(nextUnpaidIndex / ROWS_PER_PAGE);
};

const paginationStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  paddingTop: '12px',
};

const pageInfoStyles = {
  fontSize: '14px',
  color: 'text.secondary',
  minWidth: '120px',
  textAlign: 'center',
};

const PaymentSchedule = ({ isLoading, paymentSchedule, isHeloc = false, hasEscrow = false }) => {
  const initialPage = useMemo(() => getInitialPage(paymentSchedule), [paymentSchedule]);

  const [page, setPage] = useState(initialPage);

  const totalPages = Math.ceil(paymentSchedule.length / ROWS_PER_PAGE);

  const tableConfig = useMemo(
    () => getPaymentScheduleTableConfig({ isHeloc, hasEscrow }),
    [isHeloc, hasEscrow],
  );

  const paginatedData = useMemo(() => {
    const start = page * ROWS_PER_PAGE;

    return paymentSchedule.slice(start, start + ROWS_PER_PAGE);
  }, [paymentSchedule, page]);

  useEffect(() => {
    setPage(initialPage);
  }, [initialPage]);

  const onPrevPage = useCallback(() => setPage(p => Math.max(0, p - 1)), []);

  const onNextPage = useCallback(() => setPage(p => Math.min(totalPages - 1, p + 1)), [totalPages]);

  return (
    <>
      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        data={paginatedData}
        isLoading={isLoading}
        skeletonRows={5}
      />

      {!!paymentSchedule.length && (
        <Box sx={paginationStyles}>
          <Button
            data-testid={PaymentScheduleTestIds.PREV_BUTTON}
            disabled={!page}
            size="small"
            variant="outlined"
            onClick={onPrevPage}
          >
            Previous
          </Button>
          <Box sx={pageInfoStyles}>
            Page {page + 1} of {totalPages}
          </Box>
          <Button
            data-testid={PaymentScheduleTestIds.NEXT_BUTTON}
            disabled={page >= totalPages - 1}
            size="small"
            variant="outlined"
            onClick={onNextPage}
          >
            Next
          </Button>
        </Box>
      )}
    </>
  );
};

PaymentSchedule.propTypes = {
  hasEscrow: PropTypes.bool,
  isHeloc: PropTypes.bool,
  isLoading: PropTypes.bool,
  paymentSchedule: PropTypes.array,
};

export default memo(PaymentSchedule);
